import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEventHandler, ReactNode, useState } from "react";
import { InfoCircle } from "iconsax-react";
import { numberToPersianWords } from "../../../utils/currency-formater";
import { useLangContext } from "../../../context/langContext";
import { Eye, EyeSlash } from "iconsax-react";

export interface IMuiTextField {
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  handleChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: null | boolean | ReactNode;
  Example?: string;
  width?: any;
  sxStyle?: any;
  required?: boolean;
  type?: string;
  isCurrency?: boolean;
  multiline?: boolean;
  labelSx?: string;
  labelColor?: string | any;
  formatter?: (value: any) => any;
  reverseFormatter?: (value: any) => any;
  onBlur?: () => any;
  disabled?: boolean;
  inputMode?:
    | "search"
    | "text"
    | "email"
    | "tel"
    | "url"
    | "none"
    | "numeric"
    | "decimal";
}

export default function MuiTextField({
  Example,
  error,
  handleChange,
  label,
  name,
  placeholder,
  value,
  width = "100%",
  sxStyle,
  required,
  type = "text",
  multiline,
  labelSx,
  labelColor,
  formatter,
  reverseFormatter,
  inputMode = "text",
  isCurrency,
  onBlur,
  disabled,
}: Readonly<IMuiTextField>) {
  const [displayVal, setDisplayVal] = useState("");
  const { dir } = useLangContext();
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Stack direction="column" width={width}>
      {label && (
        <Typography
          variant={labelSx ? (labelSx as "labelMediumBold") : "labelMediumBold"}
          color={
            !labelColor ? (theme) => theme.palette.dominant[400] : labelColor
          }
        >
          {label}
        </Typography>
      )}
      <TextField
        dir={type === "password" ? "ltr" : isCurrency ? "ltr" : dir}
        multiline={multiline}
        minRows={multiline ? 4 : 1}
        fullWidth
        required={required}
        inputMode={inputMode}
        disabled={disabled}
        onBlur={onBlur}
        onFocus={onBlur}
        name={name}
        type={type === "password" ? (showPassword ? "text" : "password") : type}
        InputProps={{
          endAdornment: type === "password" && (
            <InputAdornment position="end">
              <IconButton
                disableRipple
                aria-label={
                  showPassword ? "hide the password" : "display the password"
                }
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& .MuiInputAdornment-root": {
              margin: 0,
              padding: 0,
              borderLeft: "none !important",
            },
            "& .MuiOutlinedInput-input:focus": {
              outline: "none !important",
            },
          },
          "& input::placeholder": {
            direction: isCurrency ? "ltr" : type === "password" ? dir : dir,
            textAlign: isCurrency ? "left" : "right", 
          },
          "& .MuiInputBase-root": {
            // backgroundColor: "var(--gray-bg) !important",
            "& input": {
              ...sxStyle,
            },
          },
        }}
        placeholder={placeholder}
        value={formatter ? formatter(value) : value}
        onChange={(e) => {
          const value = reverseFormatter
            ? reverseFormatter(e.target.value)
            : e.target.value;
          e.target.value = value;
          if (/^\d*\.?\d*$/.test(value) && isCurrency) {
            isCurrency && setDisplayVal(numberToPersianWords(e.target.value));
          }

          handleChange && handleChange(e);
        }}
        error={!!error && Boolean(error)}
        FormHelperTextProps={{
          sx: {
            mr: "6px",
          },
        }}
        helperText={
          error && Boolean(error) ? (
            error
          ) : (
            <>
              {Example && (
                <Typography
                  mr="0"
                  component={Stack}
                  alignItems="center"
                  direction="row"
                  gap="6px"
                  color={(theme) => theme.palette.dominant[300]}
                  variant="warningRegular"
                >
                  <InfoCircle
                    size="16"
                    // color={"var(--dominant-300)"}
                    variant="Outline"
                  />
                  <Typography
                    variant="warningRegular"
                    color={(theme) => theme.palette.dominant[300]}
                  >
                    {Example}
                  </Typography>
                </Typography>
              )}
              {isCurrency && displayVal && (
                <Typography
                  mr="0"
                  component={Stack}
                  alignItems="center"
                  direction="row"
                  px="6px"
                  color={(theme) => theme.palette.dominant[300]}
                  variant="warningRegular"
                >
                  <Typography
                    variant="warningRegular"
                    color={(theme) => theme.palette.dominant[300]}
                  >
                    {displayVal}
                  </Typography>
                </Typography>
              )}
            </>
          )
        }
      />
    </Stack>
  );
}
